import sampleData from "@/data/sample-data.json";

const AVATAR_COLORS = ["#4fa4ff", "#41b8d5", "#9b59b6", "#f5a623", "#27ae60", "#e74c3c", "#1e8449", "#7f77dd"];
const SOURCE_COLORS = ["#4fa4ff", "#5dade2", "#41b8d5", "#9b59b6", "#f5b6c8", "#e74c3c", "#27ae60"];

const PIPELINE_STAGES = [
	{ key: "lead", label: "Lead", typeNames: ["Lead"], color: "#4fa4ff" },
	{ key: "tender", label: "Tender/Hard Bid", typeNames: ["Tender/Hard Bid"], color: "#f5a623" },
	{ key: "rfp", label: "RFP", typeNames: ["RFP"], color: "#9b59b6" },
	{ key: "rfq", label: "RFQ", typeNames: ["RFQ"], color: "#41b8d5" },
	{ key: "estimate", label: "Estimate", typeNames: ["Estimate"], color: "#27ae60" },
	{ key: "awarded", label: "Awarded", typeNames: ["Awarded"], color: "#1e8449" },
];

const EXCLUDED_STATUSES = ["Not Pursued", "Not Awarded"];

function fn_fmtCurrency(val) {
	if(!val) return "$0";
	if (val >= 1000000) return "$" + (val / 1000000).toFixed(2) + "M";
	if (val >= 1000) return "$" + Math.round(val / 1000).toLocaleString() + "K";
	return "$" + Math.round(val).toLocaleString();
}

function fn_initials(name) {
	if (!name) return "??";
	return name.split(/\s+/).map(w => w[0]).join("").slice(0, 2).toUpperCase();
}

function fn_daysAgo(dateStr) {
	if (!dateStr) return "";
	const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 86400000);
	if (diff <= 0) return "today";
	if (diff === 1) return "1d ago";
	return diff + "d ago";
}

function fn_fmtShortDate(dateStr) {
	if (!dateStr) return "";
	const d = new Date(dateStr);
	return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function fn_isOverdue(dateStr) {
	if (!dateStr) return false;
	return new Date(dateStr) < new Date(new Date().toDateString());
}

function fn_accountName(accountId, accounts) {
	const acct = accounts.find(a => a.accountId === accountId);
	return acct ? acct.accountName : "";
}

function fn_fmtDueDate(dateStr) {
	if (!dateStr) return "";
	const d = new Date(dateStr + (dateStr.length <= 10 ? "T00:00:00" : ""));
	if (isNaN(d.getTime())) return "";
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	const yy = String(d.getFullYear()).slice(-2);
	return mm + "/" + dd + "/" + yy;
}


function fn_assigneeAvatars(opportunity, colorIx) {
	const people = [];
	const seen = new Set();
	
	const internalContactId = opportunity.internalContactId;
	const internalContactName = opportunity.internalContactName;
	
	if(internalContactId)
		{
			seen.add(internalContactId);
			people.push({
				teamMemberId: internalContactId,
				teamMemberName: internalContactName ,
			});
		}

	(opportunity.assignedTeamMembers || []).forEach(m => {
		const id = m.teamMemberId;
		if (id && seen.has(id)) return;
		if (id) seen.add(id);
		people.push({teamMemberId: m.teamMemberId,
				     teamMemberName: m.teamMemberName,
			        });
	});

	if (!people.length) {
		return [{
			initials: "??",
			avatarColor: AVATAR_COLORS[colorIx % AVATAR_COLORS.length],
			name: "",
		}];
	}

	return people.map((m, ix) => ({
		initials: fn_initials(m.teamMemberName),
		avatarColor: AVATAR_COLORS[(colorIx + ix) % AVATAR_COLORS.length],
		name: m.teamMemberName || "",
	}));
}

function fn_buildDeal(opportunity, accounts, colorIx) {
	const avatars = fn_assigneeAvatars(opportunity,colorIx);
	return {
		id: opportunity.opportunityId,
		opportunityNo: opportunity.opportunityNo,
		title: opportunity.opportunityName,
		value: fn_fmtCurrency(opportunity.value),
		valueRaw: opportunity.value || 0,
		company: opportunity.accountName,         //fn_accountName(opportunity.clientAccountId, accounts),
		stage: opportunity.opportunityTypeName,
		dueDate: fn_fmtDueDate(opportunity.dueDate),
		avatars,
		initials: avatars[0].initials,
		avatarColor: avatars[0].avatarColor,
		won: opportunity.opportunityTypeName === "Awarded" || opportunity.opportunityStatusName === "Secured",
	};
}

function fn_buildPipelineColumns(opportunities, accounts, teamMembers) {
	const active = opportunities.filter(o => !EXCLUDED_STATUSES.includes(o.opportunityStatusName));
	let colorIx = 0;

	return PIPELINE_STAGES.map(stage => {
		const deals = active
			.filter(o => stage.typeNames.includes(o.opportunityTypeName))
			.map(o => fn_buildDeal(o, accounts, teamMembers, colorIx++));

		const totalVal = active
			.filter(o => stage.typeNames.includes(o.opportunityTypeName))
			.reduce((sum, o) => sum + (o.value || 0), 0);

		return {
			key: stage.key,
			label: stage.label,
			color: stage.color,
			total: fn_fmtCurrency(totalVal),
			count: deals.length,
			deals,
		};
	});
}
function fn_buildKpis(kpis) {
	return [
		{ key: "totalPipeline", label: "Total Pipeline", value: fn_fmtCurrency(kpis.total_pipeline), trend: `+ ${kpis.total_pipeline_growth}%`, icon: "chart-line", iconBg: "#4fa4ff" },
		{ key: "openDeals", label: "Open Deals", value: String(kpis.open_deals), trend: `+ ${kpis.open_deals_growth}%`, icon: "building", iconBg: "#41b8d5" },
		{ key: "wonYtd", label: "Won This Year", value: fn_fmtCurrency(kpis.won_ytd), trend: `+ ${kpis.won_ytd_growth}%`, icon: "trophy", iconBg: "#9b59b6" },
		{ key: "conversion", label: "Conversion Rate", value: kpis.conversion_rate + "%", trend: `+ ${kpis.conversion_rate_growth}%`, icon: "calculator", iconBg: "#f5a623" },
	];
}


function fn_buildMarketSegments(opportunities, marketSegments) {
	const active = opportunities.filter(o => !EXCLUDED_STATUSES.includes(o.opportunityStatusName));
	const counts = {};

	active.forEach(o => {
		const seg = marketSegments.find(s => s.id === o.marketSegmentId);
		const name = seg ? seg.name : "Other";
		counts[name] = (counts[name] || 0) + 1;
	});

	const total = Object.values(counts).reduce((a, b) => a + b, 0) || 1;

	return Object.keys(counts).map((label, ix) => ({
		label,
		pct: Math.round((counts[label] / total) * 100),
		color: SOURCE_COLORS[ix % SOURCE_COLORS.length],
	}));
}

const PRNT_BY_ACTIVITY = {
	opportunityTask: "oppr",
	projectTask: "proj",
	accountTask: "acct",
	calendarTask: "cal",
};

function fn_startOfWeek(date) {
	const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
	const day = d.getDay(); // 0 = Sunday
	d.setDate(d.getDate() - day);
	d.setHours(0, 0, 0, 0);
	return d;
}

function fn_endOfWeek(date) {
	const start = fn_startOfWeek(date);
	const end = new Date(start);
	end.setDate(start.getDate() + 6);
	end.setHours(23, 59, 59, 999);
	return end;
}

function fn_isDueThisWeek(dateStr) {
	if (!dateStr) return false;
	const due = new Date(dateStr + "T00:00:00");
	if (isNaN(due.getTime())) return false;
	const now = new Date();
	return due >= fn_startOfWeek(now) && due <= fn_endOfWeek(now);
}

function fn_buildTasks(activities) {
	return (activities || [])
		.filter(a => a.taskStatusName !== "Completed" && fn_isDueThisWeek(a.dueDate))
		.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
		.map(a => {
			const prntType = PRNT_BY_ACTIVITY[a.activityType] || a.prntType || "oppr";
			const id = a.opportunityTaskId || a.projectTaskId || a.accountTaskId || a.calendarTaskId;
			return {
				id,
				prntType,
				text: a.taskName,
				date: fn_fmtShortDate(a.dueDate),
				done: a.taskStatusName === "Completed",
				overdue: fn_isOverdue(a.dueDate),
				taskData: { ...a },
			};
		});
}

function fn_periodRange(period) {
	const now = new Date();
	const y = now.getFullYear();
	const m = now.getMonth();

	switch (period) {
		case "lastMonth": {
			const lm = m === 0 ? 11 : m - 1;
			const ly = m === 0 ? y - 1 : y;
			return {
				start: new Date(ly, lm, 1),
				end: new Date(ly, lm + 1, 0, 23, 59, 59, 999),
				grain: "day",
			};
		}
		case "thisQuarter": {
			const qStart = Math.floor(m / 3) * 3;
			return {
				start: new Date(y, qStart, 1),
				end: new Date(y, qStart + 3, 0, 23, 59, 59, 999),
				grain: "day",
			};
		}
		case "thisYear":
			return {
				start: new Date(y, 0, 1),
				end: new Date(y, 11, 31, 23, 59, 59, 999),
				grain: "month",
			};
		case "nextYear":
			return {
				start: new Date(y + 1, 0, 1),
				end: new Date(y + 1, 11, 31, 23, 59, 59, 999),
				grain: "month",
			};
		case "thisMonth":
		default:
			return {
				start: new Date(y, m, 1),
				end: new Date(y, m + 1, 0, 23, 59, 59, 999),
				grain: "day",
			};
	}
}

/**
 * Pipeline value for each bucket in the selected period (not cumulative).
 * Each point is the sum of open-pipeline opportunity values created in that month/week only.
 */
export function fn_buildHomeDashboardData(data) {
	const src = data || sampleData;
	const { opportunities, accounts, activities, teamMembers, monthlyTotals,refData, kpis } = data;
	//const period = chartPeriod || "thisMonth";

	return {
		kpis: fn_buildKpis(kpis),
		pipelineColumns: fn_buildPipelineColumns(opportunities, accounts, teamMembers),
		marketSegments: fn_buildMarketSegments(opportunities, refData.marketSegments),
		//tasks: fn_buildTasks(activities),
		//pipelineChart: fn_buildPipelineChart(opportunities, period),
		tasks:[],
		pipelineChartData:monthlyTotals,
		opportunities,
	};
}
